import { Button } from '../ui/button';
import { ChevronRight, Mail, FileDown, MessageSquareHeart } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { trackButtonClick } from '../../utils/analytics';
import { resolveCtaClick } from '../../utils/ctaActions';
import { BRAND_GREEN } from './landingConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FinalWhistleSection — the very last block on the landing page. Lives BELOW
 * the existing Final CTA so a fully scrolled visitor gets one more friendly,
 * lower-key nudge before the footer.
 *
 * All copy + behaviour is CMS-controlled via `content.final_whistle`:
 *   enabled              — show/hide the entire section (default OFF)
 *   eyebrow              — tiny label above the headline (e.g. "ONE LAST THING")
 *   headline             — main heading
 *   subhead              — supporting line
 *   cta_text             — primary button text
 *   action_type          — 'signup' | 'scroll' (uses resolveCtaClick under the hood)
 *   scroll_target        — section id when action_type === 'scroll'
 *   prefill_weeks        — optional Pricing-slider prefill
 *   background_style     — 'light' | 'subtle' | 'dark' visual variant
 *   soft_ask.type        — 'none' | 'newsletter' | 'pdf' | 'question'
 *   soft_ask.label       — explainer copy beside the secondary ask
 *   soft_ask.button_text — secondary button label
 *   soft_ask.url         — used by 'pdf' (download link)
 */
export function FinalWhistleSection({ content, theme, goToSignup }) {
  const fw = content?.final_whistle || {};
  if (fw.enabled !== true) return null;

  const isDark = theme === 'dark' || theme === 'black';
  const bgStyle = fw.background_style || 'subtle';
  const surfaceCls = (() => {
    if (bgStyle === 'dark') return 'bg-[#0f172a] text-white';
    if (bgStyle === 'light') return 'bg-white text-gray-900 border border-gray-200';
    if (bgStyle === 'brand-green') return 'bg-gradient-to-br from-[#007a33] to-[#005c26] text-white';
    // 'subtle' default — adapts to landing theme so it doesn't fight visuals
    return isDark
      ? 'bg-gray-900/60 text-white border border-white/10'
      : 'bg-[#f8fafc] text-gray-900 border border-gray-200';
  })();
  const subheadCls = bgStyle === 'dark' || bgStyle === 'brand-green' || (bgStyle === 'subtle' && isDark)
    ? 'text-white/75'
    : 'text-gray-600';
  // On the brand-green card the green CTA disappears into the background, so
  // we invert it to a white pill with green ink — matches the standard
  // "inverted CTA on green hero" pattern used elsewhere on the page.
  const ctaCls = bgStyle === 'brand-green'
    ? 'bg-white hover:bg-gray-100 text-[#005c26]'
    : 'bg-[#007a33] hover:bg-[#005c26] text-white';
  // Eyebrow needs to invert too — brand green on green is invisible.
  const eyebrowColor = bgStyle === 'brand-green' ? '#ffffff' : BRAND_GREEN;

  return (
    <section
      data-section="final_whistle"
      data-testid="final-whistle-section"
      className={`relative z-10 px-4 sm:px-6 lg:px-8 pb-20 ${isDark ? 'bg-gray-900' : 'bg-white'}`}
    >
      <div className={`max-w-3xl mx-auto rounded-2xl px-8 py-10 sm:py-12 text-center ${surfaceCls}`}>
        {fw.eyebrow && (
          <div
            className="text-xs font-bold uppercase tracking-[0.18em] mb-3"
            style={{ color: eyebrowColor }}
            data-testid="final-whistle-eyebrow"
          >
            {fw.eyebrow}
          </div>
        )}
        <h2
          className="text-2xl sm:text-3xl font-extrabold leading-tight mb-3"
          data-testid="final-whistle-headline"
        >
          {fw.headline || "Still scrolling? You're our kind of coach."}
        </h2>
        {fw.subhead && (
          <p className={`text-base sm:text-lg mb-7 ${subheadCls}`} data-testid="final-whistle-subhead">
            {fw.subhead}
          </p>
        )}

        <Button
          size="lg"
          className={`${ctaCls} text-base px-7 py-5 rounded-xl shadow-sm`}
          onClick={() => {
            trackButtonClick('final_whistle_cta', fw.cta_text || "I'm in");
            // Reuse the same shape resolveCtaClick understands so prefill +
            // scroll-to-section work identically to other CTAs on the page.
            resolveCtaClick({
              action_type: fw.action_type,
              scroll_target: fw.scroll_target,
              prefill_weeks: fw.prefill_weeks,
            }, goToSignup);
          }}
          data-testid="final-whistle-cta-btn"
        >
          {fw.cta_text || "I'm in! Let's get started"}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>

        <SoftAsk softAsk={fw.soft_ask} subheadCls={subheadCls} />
      </div>
    </section>
  );
}

/**
 * SoftAsk — secondary low-commitment ask for visitors not ready to buy.
 * Defaults to OFF; admin enables one of: newsletter, pdf, or question.
 */
function SoftAsk({ softAsk, subheadCls }) {
  const type = softAsk?.type || 'none';
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'done' | 'error'

  if (type === 'none') return null;

  if (status === 'done') {
    return (
      <p className={`mt-6 text-sm ${subheadCls}`} data-testid="final-whistle-soft-ask-done">
        Thanks — we'll be in touch. ⚽
      </p>
    );
  }

  const submit = async (endpoint, payload) => {
    setStatus('submitting');
    try {
      await axios.post(`${API}${endpoint}`, payload);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  if (type === 'newsletter') {
    return (
      <form
        className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
        onSubmit={(e) => { e.preventDefault(); submit('/leads/newsletter', { email }); }}
        data-testid="final-whistle-soft-newsletter"
      >
        <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-300">
          <Mail className="w-4 h-4 text-gray-400" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={softAsk.label || 'Your email for a weekly coaching tip'}
            className="flex-1 bg-transparent outline-none text-sm text-gray-900"
            data-testid="final-whistle-newsletter-email"
          />
        </div>
        <Button
          type="submit"
          variant="outline"
          disabled={status === 'submitting'}
          className="text-sm border-gray-300"
          data-testid="final-whistle-newsletter-submit"
        >
          {status === 'submitting' ? 'Sending…' : (softAsk.button_text || 'Subscribe')}
        </Button>
      </form>
    );
  }

  if (type === 'pdf') {
    return (
      <a
        href={softAsk.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#007a33] hover:text-[#005c26] underline-offset-4 hover:underline"
        data-testid="final-whistle-soft-pdf"
        onClick={() => trackButtonClick('final_whistle_pdf', softAsk.label || 'Download')}
      >
        <FileDown className="w-4 h-4" />
        {softAsk.label || 'Or grab our free Sub-Off Cheat Sheet'}
      </a>
    );
  }

  if (type === 'question') {
    return (
      <form
        className="mt-6 flex flex-col gap-2 max-w-md mx-auto"
        onSubmit={(e) => { e.preventDefault(); submit('/leads/question', { email, feedback }); }}
        data-testid="final-whistle-soft-question"
      >
        <textarea
          required
          rows={2}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={softAsk.label || "What's killing your coaching week?"}
          className="px-3 py-2 bg-white rounded-lg border border-gray-300 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#007a33]/40"
          data-testid="final-whistle-question-text"
        />
        <div className="flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            className="flex-1 px-3 py-2 bg-white rounded-lg border border-gray-300 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#007a33]/40"
            data-testid="final-whistle-question-email"
          />
          <Button
            type="submit"
            variant="outline"
            disabled={status === 'submitting'}
            className="text-sm border-gray-300 inline-flex items-center gap-2"
            data-testid="final-whistle-question-submit"
          >
            <MessageSquareHeart className="w-4 h-4" />
            {status === 'submitting' ? 'Sending…' : (softAsk.button_text || 'Send')}
          </Button>
        </div>
      </form>
    );
  }
  return null;
}

export default FinalWhistleSection;
